import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import './App.css';

const IS_DEV = window.location.hostname === 'localhost';
const API = IS_DEV ? 'http://localhost:3001/api' : '/api';
const SOCKET_URL = IS_DEV ? 'http://localhost:3001' : window.location.origin;

// ─── Icons (inline SVG) ───────────────────────────────────────────────────────
const Icon = {
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  image: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
  file: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  blast: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  bot: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/><line x1="8" y1="15" x2="8" y2="15"/><line x1="16" y1="15" x2="16" y2="15"/></svg>,
  log: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/><polyline points="14 2 14 8 20 8"/></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  link: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
};

// ─── API Helper ───────────────────────────────────────────────────────────────
const api = {
  get: (url) => axios.get(`${API}${url}`).then(r => r.data),
  post: (url, data, cfg) => axios.post(`${API}${url}`, data, cfg).then(r => r.data),
  put: (url, data) => axios.put(`${API}${url}`, data).then(r => r.data),
  delete: (url) => axios.delete(`${API}${url}`).then(r => r.data),
  patch: (url) => axios.patch(`${API}${url}`).then(r => r.data),
};

function PinLogin({ onSuccess }) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [checking, setChecking] = useState(false);
  const inputRefs = useRef([]);

  const handleDigit = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);
    setError('');
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (newDigits.every(d => d !== '') && value) {
      checkPin(newDigits.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      checkPin(pasted);
    }
  };

  const checkPin = async (pin) => {
    setChecking(true);
    try {
      const res = await api.post('/auth/login', { pin });
      sessionStorage.setItem('wag_token', res.token);
      onSuccess();
    } catch (err) {
      const msg = err.response?.status === 401
        ? 'PIN salah. Coba lagi.'
        : 'Gagal verifikasi. Coba lagi.';
      setShake(true);
      setError(msg);
      setDigits(['', '', '', '', '', '']);
      setTimeout(() => { setShake(false); inputRefs.current[0]?.focus(); }, 600);
    }
    setChecking(false);
  };

  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  return (
    <div className="pin-overlay">
      <div className={`pin-card ${shake ? 'pin-shake' : ''}`}>
        <div className="pin-icon">◈</div>
        <div className="pin-title">WA Gateway</div>
        <div className="pin-subtitle">Masukkan PIN untuk melanjutkan</div>
        <form onSubmit={e => e.preventDefault()} autoComplete="off">
        <div className="pin-inputs" onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={el => inputRefs.current[i] = el}
              className={`pin-input ${d ? 'filled' : ''}`}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={e => handleDigit(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              autoComplete="off"
            />
          ))}
        </div>
        </form>
        {error && <div className="pin-error">{error}</div>}
        {checking && <div className="pin-checking">Memeriksa...</div>}
      </div>
    </div>
  );
}

function AppWrapper() {
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem('wag_token'));
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem('wag_token');
    if (!token) {
      setVerifying(false);
      return;
    }
    api.post('/auth/verify', { token })
      .then(res => {
        if (res.valid) setAuthed(true);
        else sessionStorage.removeItem('wag_token');
      })
      .catch(() => sessionStorage.removeItem('wag_token'))
      .finally(() => setVerifying(false));
  }, []);

  const lockApp = async () => {
    const token = sessionStorage.getItem('wag_token');
    sessionStorage.removeItem('wag_token');
    setAuthed(false);
    if (token) api.post('/auth/logout', { token }).catch(() => {});
  };

  if (verifying) {
    return (
      <div className="pin-overlay">
        <div className="pin-card">
          <div className="pin-icon">◈</div>
          <div className="pin-title">WA Gateway</div>
          <div className="pin-checking">Memeriksa...</div>
        </div>
      </div>
    );
  }

  if (!authed) return <PinLogin onSuccess={() => setAuthed(true)} />;
  return <App onLock={lockApp} />;
}

export default AppWrapper;

function App({ onLock }) {
  const [tab, setTab] = useState('dashboard');
  const [instances, setInstances] = useState([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState(() => {
    return sessionStorage.getItem('wag_instance') || null;
  });
  const [stats, setStats] = useState({ sent: 0, failed: 0, received: 0, blastJobs: 0 });
  const [logs, setLogs] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [toast, setToast] = useState(null);
  const [contacts, setContacts] = useState([]);
  const socketRef = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const activeInstance = instances.find(i => i.id === selectedInstanceId) || instances[0] || null;

  const setActiveInstance = (id) => {
    setSelectedInstanceId(id);
    sessionStorage.setItem('wag_instance', id);
  };

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      reconnectionAttempts: 3,
    });
    socketRef.current = socket;

    socket.on('instances', (list) => setInstances(list));

    socket.on('status', (s) => {
      setInstances(prev => prev.map(i =>
        i.id === s.instanceId ? { ...i, connected: s.connected, phone: s.phone || i.phone } : i
      ));
    });

    socket.on('qr', (data) => {
      setInstances(prev => prev.map(i =>
        i.id === data.instanceId ? { ...i, hasQR: true, qr: data.qr } : i
      ));
    });

    socket.on('stats', (s) => setStats(s));
    socket.on('log', (log) => setLogs(prev => [log, ...prev].slice(0, 100)));
    socket.on('message', (msg) => {
      setInbox(prev => {
        const entry = { ...msg, id: Date.now() + Math.random() };
        return [entry, ...prev].slice(0, 500);
      });
    });

    socket.on('instance_added', (inst) => {
      setInstances(prev => [...prev, inst]);
    });
    socket.on('instance_removed', (id) => {
      setInstances(prev => prev.filter(i => i.id !== id));
      if (selectedInstanceId === id) {
        setSelectedInstanceId(null);
        sessionStorage.removeItem('wag_instance');
      }
    });

    // Initial fetch
    api.get('/instances').then(list => {
      setInstances(list);
      if (!selectedInstanceId && list.length > 0) {
        setSelectedInstanceId(list[0].id);
      }
    }).catch(() => {});
    api.get('/stats').then(setStats).catch(() => {});
    api.get('/logs').then(setLogs).catch(() => {});
    api.get('/contacts').then(setContacts).catch(() => {});

    const pollInterval = setInterval(async () => {
      try {
        const [instancesData, statsData] = await Promise.all([
          api.get('/instances'),
          api.get('/stats'),
        ]);
        // Merge dengan state sebelumnya — pertahankan QR data URL dari socket
        setInstances(prev => instancesData.map(newInst => {
          const old = prev.find(i => i.id === newInst.id);
          if (newInst.hasQR && old?.qr) return { ...newInst, qr: old.qr };
          if (!newInst.hasQR) return { ...newInst, qr: null };
          return newInst;
        }));
        setStats(statsData);
      } catch (e) {}
    }, 4000);

    return () => {
      socket.disconnect();
      clearInterval(pollInterval);
    };
  }, []);

  const disconnect = async (instanceId) => {
    const targetId = instanceId || selectedInstanceId;
    if (!window.confirm(`Yakin ingin logout WhatsApp untuk instance "${targetId}"?`)) return;
    await api.post('/disconnect', { instance: targetId });
    setInstances(prev => prev.map(i =>
      i.id === targetId ? { ...i, connected: false, phone: null } : i
    ));
    showToast(`WhatsApp instance "${targetId}" logout`, 'info');
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '⚡' },
    { id: 'inbox', label: 'Inbox', icon: '📥' },
    { id: 'send-text', label: 'Kirim Teks', icon: '💬' },
    { id: 'send-media', label: 'Kirim Media', icon: '📎' },
    { id: 'groups', label: 'Kirim ke Grup', icon: '👥' },
    { id: 'contacts', label: 'Kontak', icon: '📇' },
    { id: 'blast', label: 'Blast', icon: '🚀' },
    { id: 'chatbot', label: 'Chatbot', icon: '🤖' },
    { id: 'instances', label: 'Instances', icon: '📱' },
    { id: 'logs', label: 'Log', icon: '📋' },
    { id: 'api-docs', label: 'API Docs', icon: '📖' },
  ];

  return (
    <div className="app">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="logo">
          <span className="logo-icon">◈</span>
          <div>
            <div className="logo-title">WA Gateway</div>
            <div className="logo-sub">Self-hosted</div>
          </div>
        </div>

        {/* Instance Selector */}
        <div className="instance-selector">
          <div className="instance-selector-label">Instance Aktif</div>
          <select
            className="form-input instance-select"
            value={selectedInstanceId || ''}
            onChange={e => setActiveInstance(e.target.value)}
          >
            {instances.map(inst => (
              <option key={inst.id} value={inst.id}>
                {inst.name} {inst.connected ? `(+${inst.phone})` : '(offline)'}
              </option>
            ))}
          </select>
        </div>

        {/* Active instance connection badge */}
        {activeInstance && (
          <div className={`conn-badge ${activeInstance.connected ? 'conn' : 'disconn'}`}>
            <span className="conn-dot" />
            {activeInstance.connected ? `+${activeInstance.phone}` : `${activeInstance.name}: Belum terhubung`}
          </div>
        )}

        <nav>
          {tabs.map(t => (
            <button
              key={t.id}
              className={`nav-btn ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <span>{t.icon}</span>
              <span>{t.label}</span>
              {t.id === 'inbox' && inbox.length > 0 && (
                <span className="nav-badge">{inbox.length > 99 ? '99+' : inbox.length}</span>
              )}
            </button>
          ))}
        </nav>

        {activeInstance && activeInstance.connected && (
          <button className="logout-btn" onClick={() => disconnect()}>
            ⏻ Logout Instance
          </button>
        )}
        <button className="lock-btn" onClick={onLock}>
          🔒 Kunci Aplikasi
        </button>
      </aside>

      {/* Main */}
      <main className="main">
        {toast && (
          <div className={`toast toast-${toast.type}`}>{toast.msg}</div>
        )}

        {tab === 'dashboard' && (
          <Dashboard instances={instances} stats={stats} logs={logs} selectedId={selectedInstanceId} onSelectInstance={setActiveInstance} />
        )}
        {tab === 'inbox' && (
          <Inbox messages={inbox} onClear={() => setInbox([])} instances={instances} selectedId={selectedInstanceId} />
        )}
        {tab === 'send-text' && (
          <SendText instances={instances} selectedId={selectedInstanceId} showToast={showToast} contacts={contacts} onContactsRefresh={() => api.get('/contacts').then(setContacts).catch(() => {})} />
        )}
        {tab === 'send-media' && (
          <SendMedia instances={instances} selectedId={selectedInstanceId} showToast={showToast} contacts={contacts} onContactsRefresh={() => api.get('/contacts').then(setContacts).catch(() => {})} />
        )}
        {tab === 'groups' && (
          <SendToGroup instances={instances} selectedId={selectedInstanceId} showToast={showToast} />
        )}
        {tab === 'contacts' && (
          <Contacts contacts={contacts} showToast={showToast} onRefresh={() => api.get('/contacts').then(setContacts).catch(() => {})} />
        )}
        {tab === 'blast' && (
          <Blast instances={instances} selectedId={selectedInstanceId} showToast={showToast} contacts={contacts} />
        )}
        {tab === 'chatbot' && (
          <Chatbot showToast={showToast} />
        )}
        {tab === 'instances' && (
          <InstancesTab instances={instances} showToast={showToast} onRefresh={() => api.get('/instances').then(setInstances).catch(() => {})} />
        )}
        {tab === 'logs' && (
          <Logs logs={logs} />
        )}
        {tab === 'api-docs' && (
          <ApiDocs />
        )}
      </main>
    </div>
  );
}

// ─── Instances Tab ────────────────────────────────────────────────────────────
function InstancesTab({ instances, showToast, onRefresh }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ id: '', name: '' });
  const [qrViewing, setQrViewing] = useState(null); // instanceId yang sedang ditampilkan QR-nya

  // Poll QR untuk instance yang belum punya QR via socket
  useEffect(() => {
    if (!qrViewing) return;
    const inst = instances.find(i => i.id === qrViewing);
    if (!inst) return;

    const interval = setInterval(async () => {
      try {
        const data = await api.get(`/instances/${qrViewing}/qr`);
        if (data.qr) {
          // Simpan QR langsung ke state
          setInstances(prev => prev.map(i =>
            i.id === qrViewing ? { ...i, hasQR: true, qr: data.qr } : i
          ));
        } else if (data.connected) {
          onRefresh();
        }
      } catch (e) {}
    }, 2000);

    return () => clearInterval(interval);
  }, [qrViewing]);

  const addInstance = async () => {
    if (!form.id || !form.name) return showToast('ID dan nama wajib diisi', 'error');
    if (!/^[a-z0-9_-]+$/i.test(form.id)) return showToast('ID hanya boleh huruf, angka, -, _', 'error');
    try {
      await api.post('/instances', form);
      showToast('✅ Instance ditambahkan — QR akan muncul di bawah');
      setForm({ id: '', name: '' });
      setAdding(false);
      onRefresh();
      // Auto-buka QR viewer untuk instance baru
      setTimeout(() => setQrViewing(form.id), 500);
    } catch (err) {
      showToast('❌ ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const removeInstance = async (id) => {
    if (instances.length <= 1) return showToast('Harus ada minimal 1 instance', 'error');
    if (!window.confirm(`Hapus instance "${id}"? Semua session akan logout.`)) return;
    try {
      await api.delete(`/instances/${id}`);
      showToast(`Instance "${id}" dihapus`, 'info');
      if (qrViewing === id) setQrViewing(null);
      onRefresh();
    } catch (err) {
      showToast('❌ ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const reconnectInstance = async (id) => {
    try {
      await api.post(`/instances/${id}/reconnect`);
      showToast(`🔄 Instance "${id}" reconnect...`, 'info');
      setQrViewing(id); // tampilkan QR setelah reconnect
    } catch (err) {
      showToast('❌ ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const setDefaultInstance = async (id) => {
    try {
      await api.post(`/instances/${id}/set-default`);
      showToast('✅ Instance diset sebagai default');
      onRefresh();
    } catch (err) {
      showToast('❌ ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const viewingInst = instances.find(i => i.id === qrViewing);
  const qrInst = viewingInst && viewingInst.qr;

  return (
    <div className="page">
      <div className="chatbot-header">
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>WhatsApp Instances</h1>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>
            Kelola beberapa nomor WhatsApp dalam satu gateway
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(!adding)}>
          {Icon.plus} Tambah Instance
        </button>
      </div>

      {adding && (
        <div className="form-card">
          <h3 style={{ marginBottom: 16 }}>Instance Baru</h3>
          <label className="form-label">ID Instance</label>
          <input
            className="form-input"
            placeholder="Contoh: support, marketing, cs-2"
            value={form.id}
            onChange={e => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
          />
          <div className="form-hint">Hanya huruf kecil, angka, -, _ (digunakan untuk folder auth)</div>

          <label className="form-label" style={{ marginTop: 12 }}>Nama Tampilan</label>
          <input
            className="form-input"
            placeholder="Contoh: CS Support"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="btn-primary" onClick={addInstance}>Simpan & Tampilkan QR</button>
            <button className="btn-secondary" onClick={() => setAdding(false)}>Batal</button>
          </div>
        </div>
      )}

      {/* QR Code Viewer */}
      {qrViewing && (
        <div className="form-card" style={{ marginTop: 16, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>QR Code: {viewingInst?.name || qrViewing}</h3>
            <button className="btn-secondary btn-sm" onClick={() => setQrViewing(null)}>✕ Tutup</button>
          </div>
          {qrInst ? (
            <div className="qr-box" style={{ display: 'inline-block' }}>
              <img src={qrInst} alt="QR Code" className="qr-img" />
            </div>
          ) : (
            <div className="qr-loading" style={{ padding: 32 }}>
              <div className="spinner" />
              <span style={{ display: 'block', marginTop: 12 }}>
                {viewingInst?.connected
                  ? '✅ Sudah terhubung — klik Reconnect untuk menampilkan QR baru'
                  : 'Menunggu QR code...'}
              </span>
            </div>
          )}
          <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 12 }}>
            Buka WhatsApp → Linked Devices → Link a Device → Scan QR di atas
          </p>
        </div>
      )}

      <div className="rules-list" style={{ marginTop: 16 }}>
        {instances.map(inst => (
          <div className="rule-card" key={inst.id}>
            <div className="rule-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span className={`job-badge ${inst.connected ? 'job-completed' : 'job-failed'}`} style={{ fontSize: 10 }}>
                  {inst.connected ? 'ONLINE' : 'OFFLINE'}
                </span>
                <div className="rule-keyword" style={{ fontSize: 15 }}>{inst.name}</div>
                {inst.isDefault && (
                  <span style={{ fontSize: 11, background: 'var(--accent)', color: '#fff', padding: '2px 8px', borderRadius: 10 }}>⭐ Default</span>
                )}
              </div>
              <code className="code-inline" style={{ fontSize: 12 }}>
                ID: {inst.id}{inst.connected ? ` • +${inst.phone}` : ''}
                {inst.hasQR && !inst.connected ? ' • QR tersedia' : ''}
              </code>
            </div>
            <div className="rule-actions">
              {!inst.isDefault && (
                <button className="btn-secondary btn-sm" onClick={() => setDefaultInstance(inst.id)} title="Jadikan default">
                  ☆ Default
                </button>
              )}
              {!inst.connected && inst.hasQR && (
                <button className="btn-secondary btn-sm" onClick={() => setQrViewing(inst.id)}>
                  📱 Lihat QR
                </button>
              )}
              <button className="btn-secondary btn-sm" onClick={() => reconnectInstance(inst.id)}>
                🔄 Reconnect
              </button>
              {instances.length > 1 && (
                <button className="icon-btn danger" onClick={() => removeInstance(inst.id)}>
                  {Icon.trash}
                </button>
              )}
            </div>
          </div>
        ))}
        {instances.length === 0 && <div className="empty">Belum ada instance</div>}
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ instances, stats, logs, selectedId, onSelectInstance }) {
  return (
    <div className="page">
      <h1 className="page-title">Dashboard</h1>

      {/* Per-instance status cards */}
      <div className="section">
        <h2 className="section-title">WhatsApp Instances</h2>
        <div className="instance-cards">
          {instances.map(inst => (
            <div
              key={inst.id}
              className={`instance-card ${selectedId === inst.id ? 'instance-selected' : ''}`}
              onClick={() => onSelectInstance(inst.id)}
            >
              <div className="instance-card-header">
                <span className={`conn-dot ${inst.connected ? 'conn' : 'disconn'}`} style={{ width: 8, height: 8 }} />
                <strong>{inst.name}</strong>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {inst.connected ? `+${inst.phone}` : inst.hasQR ? 'Menunggu scan QR...' : 'Menghubungkan...'}
              </div>
            </div>
          ))}
          {instances.length === 0 && <div className="empty">Tidak ada instance</div>}
        </div>
      </div>

      <div className="stats-grid">
        {[
          { label: 'Pesan Terkirim', value: stats.sent, color: '#25d366' },
          { label: 'Pesan Gagal', value: stats.failed, color: '#ef4444' },
          { label: 'Pesan Masuk', value: stats.received, color: '#3b82f6' },
          { label: 'Blast Jobs', value: stats.blastJobs, color: '#f59e0b' },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="section">
        <h2 className="section-title">Log Terbaru</h2>
        <div className="log-list">
          {logs.slice(0, 8).map(log => (
            <LogItem key={log.id} log={log} />
          ))}
          {logs.length === 0 && <div className="empty">Belum ada log</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Instance Selector (reusable for send forms) ──────────────────────────────
function InstancePicker({ instances, selectedId, onSelect }) {
  if (!instances || instances.length <= 1) return null;
  return (
    <div className="form-group" style={{ marginBottom: 16 }}>
      <label className="form-label">WhatsApp Instance</label>
      <select
        className="form-input"
        value={selectedId || instances[0]?.id || ''}
        onChange={e => onSelect(e.target.value)}
      >
        {instances.map(inst => (
          <option key={inst.id} value={inst.id}>
            {inst.name} {inst.connected ? `(+${inst.phone})` : '(offline)'}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── Send Text ────────────────────────────────────────────────────────────────
function SendText({ instances, selectedId, showToast, contacts, onContactsRefresh }) {
  const [localInstanceId, setLocalInstanceId] = useState(selectedId);
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState([]);
  const [deleting, setDeleting] = useState(null);

  const activeId = localInstanceId || selectedId || instances[0]?.id;

  const send = async () => {
    const inst = instances.find(i => i.id === activeId);
    if (!inst || !inst.connected) return showToast('WhatsApp belum terhubung!', 'error');
    if (!to || !message) return showToast('Nomor dan pesan wajib diisi', 'error');

    setLoading(true);
    try {
      const res = await api.post('/messages/send-text', { to, message, instance: activeId });
      showToast('✅ Pesan berhasil dikirim!');
      setSent(prev => [{
        id: res.key?.id || Date.now().toString(),
        to,
        message,
        key: res.key,
        instanceId: activeId,
        time: new Date().toISOString(),
        deleted: false,
      }, ...prev].slice(0, 50));
      setMessage('');
    } catch (err) {
      showToast('❌ Gagal: ' + (err.response?.data?.error || err.message), 'error');
    }
    setLoading(false);
  };

  const deleteMsg = async (item) => {
    if (!item.key?.id) {
      showToast('Key tidak tersedia — pastikan backend sudah terupdate', 'error');
      return;
    }
    if (!window.confirm(`Hapus pesan ini untuk semua pihak?\n\n"${item.message.substring(0, 60)}..."`)) return;

    setDeleting(item.id);
    try {
      await api.post('/messages/delete', { to: item.to, messageId: item.key.id, instance: item.instanceId });
      setSent(prev => prev.map(m => m.id === item.id ? { ...m, deleted: true } : m));
      showToast('🗑 Pesan dihapus untuk semua');
    } catch (err) {
      showToast('❌ Gagal hapus: ' + (err.response?.data?.error || err.message), 'error');
    }
    setDeleting(null);
  };

  const formatTime = (iso) => new Date(iso).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });

  return (
    <div className="page">
      <h1 className="page-title">Kirim Pesan Teks</h1>
      <div className="form-card">
        <InstancePicker instances={instances} selectedId={activeId} onSelect={setLocalInstanceId} />

        <label className="form-label">Nomor Tujuan</label>
        <input
          className="form-input"
          placeholder="628xxxxxxxxxx (format internasional)"
          value={to}
          onChange={e => setTo(e.target.value)}
        />
        <div className="form-hint">Contoh: 6281234567890 (tanpa +)</div>
        <ContactPicker contacts={contacts} onSelect={(num) => setTo(num)} onRefresh={onContactsRefresh} showToast={showToast} />

        <label className="form-label" style={{ marginTop: 20 }}>Pesan</label>
        <textarea
          className="form-textarea"
          placeholder="Ketik pesan di sini..."
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={6}
        />
        <div className="form-hint">{message.length} karakter</div>

        <button className="btn-primary" onClick={send} disabled={loading}>
          {loading ? <div className="spinner-sm" /> : <>{Icon.send} Kirim Pesan</>}
        </button>
      </div>

      <div className="info-card">
        <strong>💡 Tips:</strong> Pastikan nomor tujuan aktif dan sudah menyimpan nomor kamu, untuk mengurangi risiko spam report.
      </div>

      {sent.length > 0 && (
        <div className="sent-history">
          <div className="sent-history-header">
            <span>📤 Riwayat Terkirim</span>
            <span className="sent-count">{sent.length} pesan</span>
          </div>
          {sent.map(item => (
            <div key={item.id} className={`sent-item ${item.deleted ? 'sent-deleted' : ''}`}>
              <div className="sent-item-meta">
                <span className="sent-to">+{item.to}</span>
                <span className="sent-time">{formatTime(item.time)}</span>
                {item.instanceId && <span className="sent-instance">[{item.instanceId}]</span>}
              </div>
              <div className="sent-body">
                {item.deleted
                  ? <em>🚫 Pesan ini sudah dihapus</em>
                  : item.message}
              </div>
              {!item.deleted && (
                <button
                  className="sent-delete-btn"
                  onClick={() => deleteMsg(item)}
                  disabled={deleting === item.id}
                  title="Hapus untuk semua"
                >
                  {deleting === item.id ? '...' : '🗑 Hapus untuk Semua'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Send Media ───────────────────────────────────────────────────────────────
function SendMedia({ instances, selectedId, showToast, contacts, onContactsRefresh }) {
  const [localInstanceId, setLocalInstanceId] = useState(selectedId);
  const [to, setTo] = useState('');
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('image');
  const [loading, setLoading] = useState(false);

  const activeId = localInstanceId || selectedId || instances[0]?.id;

  const send = async () => {
    const inst = instances.find(i => i.id === activeId);
    if (!inst || !inst.connected) return showToast('WhatsApp belum terhubung!', 'error');
    if (!to || !file) return showToast('Nomor dan file wajib diisi', 'error');

    const formData = new FormData();
    formData.append('to', to);
    formData.append('caption', caption);
    formData.append('instance', activeId);
    formData.append(mode, file);

    setLoading(true);
    try {
      await api.post(`/messages/send-${mode}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast(`✅ ${mode === 'image' ? 'Gambar' : 'File'} berhasil dikirim!`);
      setFile(null);
      setCaption('');
    } catch (err) {
      showToast('❌ Gagal: ' + (err.response?.data?.error || err.message), 'error');
    }
    setLoading(false);
  };

  return (
    <div className="page">
      <h1 className="page-title">Kirim Media</h1>
      <div className="form-card">
        <InstancePicker instances={instances} selectedId={activeId} onSelect={setLocalInstanceId} />

        <div className="mode-tabs">
          <button
            className={`mode-tab ${mode === 'image' ? 'active' : ''}`}
            onClick={() => { setMode('image'); setFile(null); }}
          >
            🖼️ Gambar
          </button>
          <button
            className={`mode-tab ${mode === 'file' ? 'active' : ''}`}
            onClick={() => { setMode('file'); setFile(null); }}
          >
            📄 Dokumen
          </button>
        </div>

        <label className="form-label">Nomor Tujuan</label>
        <input
          className="form-input"
          placeholder="628xxxxxxxxxx"
          value={to}
          onChange={e => setTo(e.target.value)}
        />
        <ContactPicker contacts={contacts} onSelect={(num) => setTo(num)} onRefresh={onContactsRefresh} showToast={showToast} />

        <label className="form-label" style={{ marginTop: 16 }}>
          {mode === 'image' ? 'Pilih Gambar' : 'Pilih File'}
        </label>
        <input
          type="file"
          className="form-file"
          accept={mode === 'image' ? 'image/*' : '*/*'}
          onChange={e => setFile(e.target.files[0])}
        />
        {file && <div className="form-hint">📎 {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}

        <label className="form-label" style={{ marginTop: 16 }}>Caption (opsional)</label>
        <input
          className="form-input"
          placeholder="Tambahkan keterangan..."
          value={caption}
          onChange={e => setCaption(e.target.value)}
        />

        <button className="btn-primary" onClick={send} disabled={loading}>
          {loading ? <div className="spinner-sm" /> : <>{Icon.send} Kirim</>}
        </button>
      </div>
    </div>
  );
}

// ─── Send To Group ────────────────────────────────────────────────────────────
function SendToGroup({ instances, selectedId, showToast }) {
  const [localInstanceId, setLocalInstanceId] = useState(selectedId);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [mode, setMode] = useState('text');
  const [search, setSearch] = useState('');

  const activeId = localInstanceId || selectedId || instances[0]?.id;
  const inst = instances.find(i => i.id === activeId);

  const fetchGroups = async () => {
    if (!inst || !inst.connected) return showToast('WhatsApp belum terhubung!', 'error');
    setFetching(true);
    try {
      const data = await api.get(`/groups?instance=${activeId}`);
      setGroups(data);
      showToast(`✅ ${data.length} grup ditemukan`);
    } catch (err) {
      showToast('❌ Gagal ambil grup: ' + err.message, 'error');
    }
    setFetching(false);
  };

  const send = async () => {
    if (!inst || !inst.connected) return showToast('WhatsApp belum terhubung!', 'error');
    if (!selectedGroup) return showToast('Pilih grup dulu!', 'error');

    setLoading(true);
    try {
      if (mode === 'text') {
        if (!message) return showToast('Pesan tidak boleh kosong', 'error');
        await api.post('/messages/send-text', { to: selectedGroup, message, instance: activeId });
        showToast('✅ Pesan terkirim ke grup!');
        setMessage('');
      } else if (mode === 'image' || mode === 'file') {
        if (!file) return showToast('Pilih file dulu!', 'error');
        const formData = new FormData();
        formData.append('to', selectedGroup);
        formData.append('caption', caption);
        formData.append('instance', activeId);
        formData.append(mode, file);
        await api.post(`/messages/send-${mode}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast(`✅ ${mode === 'image' ? 'Gambar' : 'File'} terkirim ke grup!`);
        setFile(null);
        setCaption('');
      }
    } catch (err) {
      showToast('❌ Gagal: ' + (err.response?.data?.error || err.message), 'error');
    }
    setLoading(false);
  };

  const filteredGroups = groups.filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase())
  );

  const selectedGroupData = groups.find(g => g.id === selectedGroup);

  return (
    <div className="page">
      <h1 className="page-title">Kirim ke Grup</h1>

      <div className="form-card">
        <InstancePicker instances={instances} selectedId={activeId} onSelect={setLocalInstanceId} />

        <div className="group-fetch-row">
          <div>
            <div className="form-label">Daftar Grup WhatsApp</div>
            <div className="form-hint">Klik tombol untuk memuat grup yang kamu ikuti</div>
          </div>
          <button className="btn-primary" style={{ marginTop: 0 }} onClick={fetchGroups} disabled={fetching}>
            {fetching ? <><div className="spinner-sm" /> Memuat...</> : '🔄 Muat Grup'}
          </button>
        </div>

        {groups.length > 0 && (
          <>
            <input
              className="form-input"
              placeholder="🔍 Cari nama grup..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ marginTop: 12 }}
            />

            <div className="group-list">
              {filteredGroups.map(g => (
                <div
                  key={g.id}
                  className={`group-item ${selectedGroup === g.id ? 'selected' : ''}`}
                  onClick={() => setSelectedGroup(g.id)}
                >
                  <div className="group-avatar">
                    {g.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="group-info">
                    <div className="group-name">{g.name}</div>
                    <div className="group-meta">{g.participants} anggota</div>
                    <div className="group-id-display" title="Copy ID Grup" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(g.id); showToast('📋 ID Grup dicopy!'); }}>
                      📋 {g.id}
                    </div>
                  </div>
                  {selectedGroup === g.id && (
                    <span className="group-check">✓</span>
                  )}
                </div>
              ))}
              {filteredGroups.length === 0 && (
                <div className="empty">Grup tidak ditemukan</div>
              )}
            </div>
          </>
        )}

        {groups.length === 0 && (
          <div className="empty" style={{ paddingTop: 16 }}>
            Klik "Muat Grup" untuk menampilkan daftar grup
          </div>
        )}
      </div>

      {selectedGroup && (
        <div className="form-card">
          <div className="selected-group-banner">
            👥 Mengirim ke: <strong>{selectedGroupData?.name}</strong>
            <span className="group-id-badge">{selectedGroupData?.participants} anggota</span>
          </div>

          <div className="mode-tabs" style={{ marginTop: 16 }}>
            {[
              { id: 'text', label: '💬 Teks' },
              { id: 'image', label: '🖼️ Gambar' },
              { id: 'file', label: '📄 File' },
            ].map(m => (
              <button
                key={m.id}
                className={`mode-tab ${mode === m.id ? 'active' : ''}`}
                onClick={() => { setMode(m.id); setFile(null); }}
              >
                {m.label}
              </button>
            ))}
          </div>

          {mode === 'text' && (
            <>
              <label className="form-label">Pesan</label>
              <textarea
                className="form-textarea"
                placeholder="Ketik pesan untuk grup..."
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={5}
              />
              <div className="form-hint">{message.length} karakter</div>
            </>
          )}

          {(mode === 'image' || mode === 'file') && (
            <>
              <label className="form-label">{mode === 'image' ? 'Pilih Gambar' : 'Pilih File'}</label>
              <input
                type="file"
                className="form-file"
                accept={mode === 'image' ? 'image/*' : '*/*'}
                onChange={e => setFile(e.target.files[0])}
              />
              {file && <div className="form-hint">📎 {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}

              <label className="form-label" style={{ marginTop: 12 }}>Caption (opsional)</label>
              <input
                className="form-input"
                placeholder="Tambahkan keterangan..."
                value={caption}
                onChange={e => setCaption(e.target.value)}
              />
            </>
          )}

          <button className="btn-primary" onClick={send} disabled={loading}>
            {loading ? <div className="spinner-sm" /> : <>{Icon.send} Kirim ke Grup</>}
          </button>
        </div>
      )}

      <div className="info-card">
        <strong>📋 Cara Pakai:</strong><br />
        1. Pilih instance WhatsApp<br />
        2. Klik <strong>Muat Grup</strong> untuk ambil daftar grup<br />
        3. Pilih grup tujuan dari daftar<br />
        4. Pilih jenis pesan (teks/gambar/file)<br />
        5. Klik Kirim
      </div>
    </div>
  );
}

// ─── Blast ────────────────────────────────────────────────────────────────────
function Blast({ instances, selectedId, showToast, contacts }) {
  const [localInstanceId, setLocalInstanceId] = useState(selectedId);
  const [numbers, setNumbers] = useState('');
  const [message, setMessage] = useState('');
  const [delay, setDelay] = useState(4000);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);

  const activeId = localInstanceId || selectedId || instances[0]?.id;

  useEffect(() => {
    api.get('/blast/jobs').then(setJobs).catch(() => {});
  }, []);

  const send = async () => {
    const inst = instances.find(i => i.id === activeId);
    if (!inst || !inst.connected) return showToast('WhatsApp belum terhubung!', 'error');
    const numList = numbers.split('\n').map(n => n.trim()).filter(Boolean);
    if (numList.length === 0 || !message) return showToast('Nomor dan pesan wajib diisi', 'error');
    if (numList.length > 500) return showToast('Maksimal 500 nomor per blast', 'error');

    setLoading(true);
    try {
      const result = await api.post('/blast/send', { numbers: numList, message, delay, instance: activeId });
      showToast(`🚀 Blast dimulai! ${numList.length} nomor`);
      setJobs(prev => [{
        id: result.jobId,
        total: numList.length,
        sent: 0,
        failed: 0,
        status: 'running',
        message: message.substring(0, 50),
        instanceId: activeId
      }, ...prev]);
    } catch (err) {
      showToast('❌ ' + (err.response?.data?.error || err.message), 'error');
    }
    setLoading(false);
  };

  const numCount = numbers.split('\n').filter(n => n.trim()).length;

  return (
    <div className="page">
      <h1 className="page-title">Blast Pesan</h1>

      <div className="warning-card">
        ⚠️ <strong>Peringatan:</strong> Blast ke banyak nomor berisiko ban. Gunakan delay yang cukup (min. 3 detik) dan hindari pesan yang sama berulang.
      </div>

      <div className="blast-grid">
        <div className="form-card">
          <InstancePicker instances={instances} selectedId={activeId} onSelect={setLocalInstanceId} />

          <label className="form-label">Daftar Nomor</label>
          <textarea
            className="form-textarea"
            placeholder={"6281234567890\n6289876543210\n628..."}
            value={numbers}
            onChange={e => setNumbers(e.target.value)}
            rows={8}
          />
          <div className="form-hint">{numCount} nomor</div>
          <ContactPicker multi contacts={contacts} onSelect={(num) => setNumbers(prev => prev ? prev + '\n' + num : num)} showToast={showToast} />

          <label className="form-label" style={{ marginTop: 16 }}>Pesan</label>
          <textarea
            className="form-textarea"
            placeholder="Pesan yang akan dikirim..."
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={4}
          />

          <label className="form-label" style={{ marginTop: 16 }}>
            Delay antar pesan: <strong>{(delay / 1000).toFixed(1)}s</strong>
          </label>
          <input
            type="range" min="2000" max="15000" step="500"
            value={delay}
            onChange={e => setDelay(Number(e.target.value))}
            className="form-range"
          />
          <div className="form-hint">Semakin besar delay = semakin aman dari ban</div>

          <button className="btn-primary" onClick={send} disabled={loading}>
            {loading ? <div className="spinner-sm" /> : <>{Icon.blast} Mulai Blast</>}
          </button>
        </div>

        <div className="section">
          <h2 className="section-title">Riwayat Blast</h2>
          {jobs.length === 0 && <div className="empty">Belum ada blast</div>}
          {jobs.map(job => (
            <div className="blast-job" key={job.id}>
              <div className="blast-job-header">
                <span className={`job-badge job-${job.status}`}>{job.status}</span>
                <span className="blast-job-msg">{job.message}</span>
                {job.instanceId && <span className="blast-job-instance">[{job.instanceId}]</span>}
              </div>
              <div className="blast-job-stats">
                <span>Total: {job.total}</span>
                <span style={{ color: '#25d366' }}>✓ {job.sent}</span>
                <span style={{ color: '#ef4444' }}>✗ {job.failed}</span>
              </div>
              {job.status === 'running' && (
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${((job.sent + job.failed) / job.total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Chatbot ──────────────────────────────────────────────────────────────────
function Chatbot({ showToast }) {
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState({ keyword: '', matchType: 'contains', reply: '' });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get('/chatbot/rules').then(setRules).catch(() => {});
  }, []);

  const addRule = async () => {
    if (!form.keyword || !form.reply) return showToast('Keyword dan reply wajib diisi', 'error');
    try {
      const rule = await api.post('/chatbot/rules', form);
      setRules(prev => [...prev, rule]);
      setForm({ keyword: '', matchType: 'contains', reply: '' });
      setAdding(false);
      showToast('✅ Rule ditambahkan');
    } catch (err) {
      showToast('❌ ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const toggle = async (id) => {
    const updated = await api.patch(`/chatbot/rules/${id}/toggle`);
    setRules(prev => prev.map(r => r.id === id ? updated : r));
  };

  const deleteRule = async (id) => {
    await api.delete(`/chatbot/rules/${id}`);
    setRules(prev => prev.filter(r => r.id !== id));
    showToast('Rule dihapus', 'info');
  };

  return (
    <div className="page">
      <h1 className="page-title">Auto Reply / Chatbot</h1>

      <div className="chatbot-header">
        <p>Atur balasan otomatis berdasarkan keyword pesan masuk.</p>
        <button className="btn-primary" onClick={() => setAdding(!adding)}>
          {Icon.plus} Tambah Rule
        </button>
      </div>

      {adding && (
        <div className="form-card">
          <h3 style={{ marginBottom: 16 }}>Rule Baru</h3>
          <label className="form-label">Keyword</label>
          <input
            className="form-input"
            placeholder="Contoh: halo, harga, promo"
            value={form.keyword}
            onChange={e => setForm({ ...form, keyword: e.target.value })}
          />

          <label className="form-label" style={{ marginTop: 12 }}>Tipe Pencocokan</label>
          <select
            className="form-input"
            value={form.matchType}
            onChange={e => setForm({ ...form, matchType: e.target.value })}
          >
            <option value="contains">Mengandung keyword</option>
            <option value="exact">Sama persis</option>
            <option value="startsWith">Diawali keyword</option>
          </select>

          <label className="form-label" style={{ marginTop: 12 }}>Balasan Otomatis</label>
          <textarea
            className="form-textarea"
            placeholder="Pesan balasan..."
            value={form.reply}
            onChange={e => setForm({ ...form, reply: e.target.value })}
            rows={3}
          />

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn-primary" onClick={addRule}>Simpan</button>
            <button className="btn-secondary" onClick={() => setAdding(false)}>Batal</button>
          </div>
        </div>
      )}

      <div className="rules-list">
        {rules.length === 0 && <div className="empty">Belum ada rule</div>}
        {rules.map(rule => (
          <div className={`rule-card ${!rule.active ? 'inactive' : ''}`} key={rule.id}>
            <div className="rule-info">
              <div className="rule-keyword">"{rule.keyword}"</div>
              <div className="rule-type">{rule.matchType}</div>
              <div className="rule-reply">→ {rule.reply}</div>
            </div>
            <div className="rule-actions">
              <button
                className={`toggle-btn ${rule.active ? 'on' : 'off'}`}
                onClick={() => toggle(rule.id)}
              >
                {rule.active ? 'ON' : 'OFF'}
              </button>
              <button className="icon-btn danger" onClick={() => deleteRule(rule.id)}>
                {Icon.trash}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Inbox ────────────────────────────────────────────────────────────────────
function Inbox({ messages, onClear, instances, selectedId }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const conversations = messages.reduce((acc, msg) => {
    const key = msg.from;
    if (!acc[key]) {
      acc[key] = { from: key, instanceId: msg.instanceId, messages: [], lastTime: msg.time, unread: 0 };
    }
    acc[key].messages.push(msg);
    if (new Date(msg.time) > new Date(acc[key].lastTime)) {
      acc[key].lastTime = msg.time;
    }
    acc[key].unread++;
    return acc;
  }, {});

  const convList = Object.values(conversations)
    .sort((a, b) => new Date(b.lastTime) - new Date(a.lastTime))
    .filter(c => !search || c.from.includes(search));

  const activeConv = selected ? conversations[selected] : null;

  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    return isToday
      ? d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short' }) + ' ' +
        d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="page inbox-page">
      <div className="inbox-header">
        <h1 className="page-title" style={{ margin: 0 }}>Inbox</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className="inbox-count">{messages.length} pesan</span>
          {messages.length > 0 && (
            <button className="btn-secondary btn-sm" onClick={onClear}>
              🗑 Hapus Semua
            </button>
          )}
        </div>
      </div>

      {messages.length === 0 ? (
        <div className="inbox-empty">
          <div className="inbox-empty-icon">📭</div>
          <div>Belum ada pesan masuk</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            Pesan yang diterima WhatsApp akan muncul di sini secara real-time
          </div>
        </div>
      ) : (
        <div className="inbox-layout">
          <div className="inbox-contacts">
            <input
              className="form-input inbox-search"
              placeholder="🔍 Cari nomor..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div className="contact-list">
              {convList.map(conv => (
                <div
                  key={conv.from}
                  className={`contact-item ${selected === conv.from ? 'active' : ''}`}
                  onClick={() => setSelected(conv.from)}
                >
                  <div className="contact-avatar">
                    {conv.from.charAt(0)}
                  </div>
                  <div className="contact-info">
                    <div className="contact-name">
                      +{conv.from}
                      {conv.instanceId && <span className="instance-tag">[{conv.instanceId}]</span>}
                    </div>
                    <div className="contact-preview">
                      {conv.messages[0].body
                        ? conv.messages[0].body.substring(0, 40) + (conv.messages[0].body.length > 40 ? '…' : '')
                        : '📎 Media'}
                    </div>
                  </div>
                  <div className="contact-meta">
                    <div className="contact-time">{formatTime(conv.lastTime)}</div>
                    <div className="contact-badge">{conv.messages.length}</div>
                  </div>
                </div>
              ))}
              {convList.length === 0 && (
                <div className="empty" style={{ padding: '24px 16px' }}>Tidak ditemukan</div>
              )}
            </div>
          </div>

          <div className="inbox-messages">
            {!selected ? (
              <div className="inbox-select-hint">
                <div style={{ fontSize: 32, marginBottom: 12 }}>👈</div>
                <div>Pilih kontak untuk melihat pesan</div>
              </div>
            ) : (
              <>
                <div className="inbox-msg-header">
                  <div className="contact-avatar" style={{ width: 34, height: 34, fontSize: 13 }}>
                    {selected.charAt(0)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>+{selected}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {activeConv.messages.length} pesan
                    </div>
                  </div>
                  <button
                    className="btn-secondary btn-sm"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => setSelected(null)}
                  >
                    ✕
                  </button>
                </div>
                <div className="msg-list">
                  {[...activeConv.messages].reverse().map((msg, i) => (
                    <div key={i} className="msg-bubble">
                      <div className="msg-body">{msg.body || <em style={{ color: 'var(--text-muted)' }}>📎 Pesan media (tidak ada teks)</em>}</div>
                      <div className="msg-time">{formatTime(msg.time)}</div>
                      {msg.instanceId && <div className="msg-instance">via {msg.instanceId}</div>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Contacts ─────────────────────────────────────────────────────────────────
function Contacts({ contacts, showToast, onRefresh }) {
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', number: '', type: 'personal' });
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const resetForm = () => {
    setForm({ name: '', number: '', type: 'personal' });
    setEditing(null);
    setAdding(false);
  };

  const save = async () => {
    if (!form.name || !form.number) return showToast('Nama dan nomor wajib diisi', 'error');
    try {
      if (editing) {
        await api.put(`/contacts/${editing.id}`, form);
        showToast('✅ Kontak diupdate');
      } else {
        await api.post('/contacts', form);
        showToast('✅ Kontak ditambahkan');
      }
      resetForm();
      onRefresh();
    } catch (err) {
      showToast('❌ ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const deleteContact = async (c) => {
    if (!window.confirm(`Hapus kontak "${c.name}"?`)) return;
    await api.delete(`/contacts/${c.id}`);
    onRefresh();
    showToast('🗑 Kontak dihapus', 'info');
  };

  const filtered = contacts.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.number.includes(q);
  });

  return (
    <div className="page">
      <div className="chatbot-header">
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>Kontak</h1>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>
            Simpan nomor personal & grup favorit untuk akses cepat
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(!adding)}>
          {Icon.plus} Tambah Kontak
        </button>
      </div>

      {(adding || editing) && (
        <div className="form-card">
          <h3 style={{ marginBottom: 16 }}>{editing ? 'Edit Kontak' : 'Kontak Baru'}</h3>

          <div className="mode-tabs" style={{ marginBottom: 16 }}>
            {['personal', 'group'].map(t => (
              <button
                key={t}
                className={`mode-tab ${form.type === t ? 'active' : ''}`}
                onClick={() => setForm({ ...form, type: t })}
              >
                {t === 'personal' ? '👤 Personal' : '👥 Grup'}
              </button>
            ))}
          </div>

          <label className="form-label">Nama</label>
          <input
            className="form-input"
            placeholder={form.type === 'personal' ? 'Contoh: John Doe' : 'Contoh: Grup Keluarga'}
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />

          <label className="form-label" style={{ marginTop: 12 }}>
            {form.type === 'personal' ? 'Nomor WhatsApp' : 'Kode Grup (JID)'}
          </label>
          <input
            className="form-input"
            placeholder={form.type === 'personal' ? '628xxxxxxxxxx' : '120363xxx@g.us'}
            value={form.number}
            onChange={e => setForm({ ...form, number: e.target.value })}
          />
          <div className="form-hint">
            {form.type === 'personal'
              ? 'Format internasional tanpa +'
              : 'Bisa didapat dari tab Kirim ke Grup → klik ID Grup'}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="btn-primary" onClick={save}>Simpan</button>
            <button className="btn-secondary" onClick={resetForm}>Batal</button>
          </div>
        </div>
      )}

      <input
        className="form-input"
        placeholder="🔍 Cari kontak..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      {contacts.length === 0 && !adding && (
        <div className="empty" style={{ padding: '40px 0' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📇</div>
          <div>Belum ada kontak tersimpan</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            Tambahkan kontak personal atau grup untuk akses cepat saat mengirim pesan
          </div>
        </div>
      )}

      {filtered.length === 0 && contacts.length > 0 && (
        <div className="empty">Kontak tidak ditemukan</div>
      )}

      <div className="rules-list">
        {filtered.map(c => (
          <div className="rule-card" key={c.id}>
            <div className="rule-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span className={`job-badge job-${c.type === 'group' ? 'running' : 'completed'}`} style={{ fontSize: 10 }}>
                  {c.type === 'group' ? 'GRUP' : 'PERSONAL'}
                </span>
                <div className="rule-keyword" style={{ fontSize: 15 }}>{c.name}</div>
              </div>
              <code className="code-inline" style={{ fontSize: 12 }}>{c.number}</code>
            </div>
            <div className="rule-actions">
              <button
                className="btn-secondary btn-sm"
                title="Copy nomor"
                onClick={() => {
                  navigator.clipboard.writeText(c.number);
                  showToast('📋 Nomor dicopy!', 'info');
                }}
              >
                📋
              </button>
              <button
                className="icon-btn"
                title="Edit"
                onClick={() => { setEditing(c); setForm({ name: c.name, number: c.number, type: c.type }); setAdding(false); }}
              >
                ✏️
              </button>
              <button className="icon-btn danger" onClick={() => deleteContact(c)}>
                {Icon.trash}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ContactPicker ─────────────────────────────────────────────────────────────
function ContactPicker({ contacts, onSelect, onRefresh, showToast, multi }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ marginTop: 8, position: 'relative' }}>
      <button
        type="button"
        className="btn-secondary btn-sm"
        onClick={() => setOpen(!open)}
      >
        📇 {multi ? 'Tambah dari Kontak' : 'Pilih dari Kontak'}
      </button>

      {open && (
        <div className="contact-picker-dropdown">
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Pilih Kontak</span>
            <button
              className="icon-btn"
              style={{ fontSize: 14 }}
              onClick={() => setOpen(false)}
            >✕</button>
          </div>
          <div className="contact-picker-list">
            {contacts.length === 0 ? (
              <div className="empty" style={{ padding: '16px', fontSize: 12 }}>
                Belum ada kontak. Tambahkan di tab Kontak.
              </div>
            ) : (
              contacts.map(c => (
                <div
                  key={c.id}
                  className="contact-picker-item"
                  onClick={() => {
                    onSelect(c.number);
                    if (!multi) setOpen(false);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{
                      width: 28, height: 28, borderRadius: '50%', background: c.type === 'group' ? 'var(--accent)' : 'var(--border)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0, color: c.type === 'group' ? '#fff' : 'var(--text-muted)'
                    }}>
                      {c.type === 'group' ? '👥' : '👤'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.name}
                      </div>
                      <code style={{ fontSize: 10, color: 'var(--text-muted)' }}>{c.number}</code>
                    </div>
                    <span className="job-badge" style={{ fontSize: 9, flexShrink: 0 }}>
                      {c.type === 'group' ? 'GRUP' : 'PERSONAL'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Logs ─────────────────────────────────────────────────────────────────────
function Logs({ logs }) {
  return (
    <div className="page">
      <h1 className="page-title">Activity Log</h1>
      <div className="log-list-full">
        {logs.length === 0 && <div className="empty">Belum ada log</div>}
        {logs.map(log => <LogItem key={log.id} log={log} />)}
      </div>
    </div>
  );
}

function LogItem({ log }) {
  const colors = {
    success: '#25d366',
    error: '#ef4444',
    info: '#3b82f6',
    warning: '#f59e0b'
  };
  const icons = { success: '✓', error: '✗', info: '•', warning: '⚠' };
  const time = new Date(log.time).toLocaleTimeString('id-ID');

  return (
    <div className="log-item">
      <span className="log-icon" style={{ color: colors[log.type] }}>
        {icons[log.type]}
      </span>
      <span className="log-msg">{log.message}</span>
      <span className="log-time">{time}</span>
    </div>
  );
}

// ─── API Docs ─────────────────────────────────────────────────────────────────
function ApiDocs() {
  const base = 'http://YOUR_SERVER_IP:3001';
  const endpoints = [
    {
      method: 'GET', path: '/api/instances',
      desc: 'List semua WhatsApp instances beserta statusnya',
      example: `curl ${base}/api/instances`
    },
    {
      method: 'POST', path: '/api/instances',
      desc: 'Tambah instance WhatsApp baru',
      example: `curl -X POST ${base}/api/instances \\\n  -H "Content-Type: application/json" \\\n  -d '{"id":"support","name":"CS Support"}'`
    },
    {
      method: 'DELETE', path: '/api/instances/:id',
      desc: 'Hapus instance WhatsApp',
      example: `curl -X DELETE ${base}/api/instances/support`
    },
    {
      method: 'GET', path: '/api/status',
      desc: 'Cek status koneksi WhatsApp (pakai ?instance=id)',
      example: `curl "${base}/api/status?instance=default"`
    },
    {
      method: 'GET', path: '/api/qr',
      desc: 'Ambil QR code (pakai ?instance=id)',
      example: `curl "${base}/api/qr?instance=default"`
    },
    {
      method: 'GET', path: '/api/groups',
      desc: 'Ambil semua grup yang diikuti (pakai ?instance=id)',
      example: `curl "${base}/api/groups?instance=default"`
    },
    {
      method: 'POST', path: '/api/messages/send-text',
      desc: 'Kirim pesan teks (pakai instance di body)',
      example: `curl -X POST ${base}/api/messages/send-text \\\n  -H "Content-Type: application/json" \\\n  -d '{"to":"628xxx","message":"Halo!","instance":"default"}'`
    },
    {
      method: 'POST', path: '/api/messages/send-image',
      desc: 'Kirim gambar (pakai instance di form data)',
      example: `curl -X POST ${base}/api/messages/send-image \\\n  -F "to=628xxx" \\\n  -F "instance=default" \\\n  -F "caption=Foto ini" \\\n  -F "image=@/path/gambar.jpg"`
    },
    {
      method: 'POST', path: '/api/messages/send-file',
      desc: 'Kirim file/dokumen (pakai instance di form data)',
      example: `curl -X POST ${base}/api/messages/send-file \\\n  -F "to=628xxx" \\\n  -F "instance=default" \\\n  -F "caption=Dokumen ini" \\\n  -F "file=@/path/dokumen.pdf"`
    },
    {
      method: 'POST', path: '/api/blast/send',
      desc: 'Blast pesan (pakai instance di body)',
      example: `curl -X POST ${base}/api/blast/send \\\n  -H "Content-Type: application/json" \\\n  -d '{"numbers":["628xxx"],"message":"Promo!","delay":4000,"instance":"default"}'`
    },
  ];

  return (
    <div className="page">
      <h1 className="page-title">API Documentation</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
        Base URL: <code className="code-inline">{base}</code>
      </p>
      {endpoints.map((ep, i) => (
        <div className="api-card" key={i}>
          <div className="api-header">
            <span className={`method-badge method-${ep.method}`}>{ep.method}</span>
            <code className="api-path">{ep.path}</code>
          </div>
          <p className="api-desc">{ep.desc}</p>
          <pre className="code-block">{ep.example}</pre>
        </div>
      ))}
    </div>
  );
}
